import { Dialog, DialogContent } from '@tunein/react-ui';
import clsx from 'clsx';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import CloseButton from '../button/CloseButton';
import css from './common-dialog.module.scss';
import darkThemeCss from './dark-theme-dialog.module.scss';

type DialogContentProps = ComponentProps<typeof DialogContent>;
type DialogContentStyle = DialogContentProps['style'];
type DialogOverlayStyle = DialogContentProps['overlayStyle'];
type DialogPortalStyle = DialogContentProps['portalStyle'];
type CSSVariableStyle = CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

interface CommonDialogClassNames {
  body?: string;
  content?: string;
  overlay?: string;
  portal?: string;
}

interface CommonDialogStyles {
  body?: CSSProperties;
  content?: DialogContentStyle;
  overlay?: DialogOverlayStyle;
  portal?: CSSVariableStyle;
}

interface CommonDialogCloseButtonProps {
  id?: string;
  iconKey?: string;
  styles?: CSSProperties;
  testId?: string;
  className?: string;
  useFontIcon?: boolean;
}

interface CommonDialogProps
  extends Omit<
    DialogContentProps,
    | 'children'
    | 'className'
    | 'style'
    | 'overlayClassName'
    | 'overlayStyle'
    | 'portalClassName'
    | 'portalStyle'
  > {
  dialogOpen: boolean;
  handleDialogClose?: () => void;
  children: ReactNode;
  classNames?: CommonDialogClassNames;
  styles?: CommonDialogStyles;
  closeButtonProps?: CommonDialogCloseButtonProps;
  hideCloseButton?: boolean;
  darkTheme?: boolean;
  variant?: 'narrow' | 'wide';
  fullScreen?: boolean;
  zIndex?: number | string;
  maxWidth?: number | string;
  preventOverlayClose?: boolean;
}

const normalizeDimension = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

const getOverlayZIndex = (value: number | string) => {
  if (typeof value === 'number') {
    return value - 1;
  }

  return `calc(${value} - 1)`;
};

function CommonDialog({
  dialogOpen,
  handleDialogClose,
  children,
  classNames,
  styles,
  closeButtonProps,
  hideCloseButton = false,
  darkTheme = false,
  variant = 'narrow',
  fullScreen = false,
  zIndex = 1500,
  maxWidth,
  preventOverlayClose = false,
  ...other
}: CommonDialogProps) {
  const portalStyle: CSSVariableStyle = {
    ...styles?.portal,
    ...(maxWidth !== undefined
      ? { '--common-dialog-max-width': normalizeDimension(maxWidth) }
      : null),
    ...(zIndex !== undefined
      ? {
          '--common-dialog-z-index': zIndex,
          '--common-dialog-overlay-z-index': getOverlayZIndex(zIndex),
        }
      : null),
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      handleDialogClose?.();
    }
  };

  return (
    <Dialog
      modal={preventOverlayClose}
      open={dialogOpen}
      disablePointerDismissal={preventOverlayClose}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className={clsx(
          css.content,
          variant === 'wide' && css.contentWide,
          fullScreen && css.contentFullScreen,
          classNames?.content,
        )}
        style={styles?.content as DialogContentStyle}
        overlayClassName={clsx(css.overlay, classNames?.overlay)}
        overlayStyle={styles?.overlay as DialogOverlayStyle}
        portalClassName={classNames?.portal}
        portalStyle={portalStyle as DialogPortalStyle}
        {...other}
      >
        {!hideCloseButton && (
          <CloseButton
            hasDarkTheme={darkTheme}
            handleDialogClose={handleDialogClose}
            {...closeButtonProps}
          />
        )}
        <div
          className={clsx(
            css.dialogBody,
            fullScreen && css.bodyFullScreen,
            darkTheme && darkThemeCss.dialogBody,
            classNames?.body,
          )}
          style={styles?.body}
        >
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CommonDialog;
